<template>
    <div>
        <Panel
            :title="$t('Trustpilot Accounts')"
            :add-button="canEdit ? $t('Add Account') : ''"
            :delete-button="selected.length > 0"
            :page="page"
            :total-rows="totalRows"
            @change="changePage"
            @delete="handleDelete"
            @input="openForm(-1)"
        >
            <table id="trustpilotaccounts_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Name') }}</th>
                        <th>{{ $t('Webhook URL') }}</th>
                        <th></th>
                        <th v-if="canEdit"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="account in accounts">
                        <td class="primary" @click="openForm(account.accountId)">
                            {{ account.accountName }}
                        </td>
                        <td class="select-all break-word">
                            {{
                                formatWebhookUri(
                                    account.apiKey,
                                    account.apiSecret,
                                    account.organisationId,
                                    account.accountId,
                                )
                            }}
                        </td>
                        <td>
                            <cic-button
                                :title="
                                    $t('Copy URL and input into Trustpilot \'Webhook Notifications\' service reviews')
                                "
                                class="copy-button"
                                @click="copyWebhookUrl(account)"
                            >
                                {{ $t('Copy') }}
                            </cic-button>
                        </td>
                        <td v-if="canEdit" class="td-checkbox">
                            <cic-checkbox
                                v-if="canEdit"
                                :value="selected.includes(account.accountId)"
                                @click="handleSelected(account.accountId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </Panel>

        <EditTrustpilotAccount
            v-if="item !== null && !$store.showLoadingOverlay"
            :item="item"
            @blur="closePopup"
            @input="saveItem"
            @change="updateFieldValue"
        />

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import Panel from '@/components/container/Panel';
    import TableUtils from '@/mixins/TableUtils';
    import { baseUrl, deleteItems, load, save } from '@/utils/Http';
    import { showErrorMessage, showValidationMessage } from '@/utils/Notification';
    import EditTrustpilotAccount from './EditTrustpilotAccount';

    export default {
        name: 'TrustpilotAccounts',
        components: {
            CicButton,
            CicCheckbox,
            EditTrustpilotAccount,
            Panel,
        },
        mixins: [TableUtils],
        props: {
            authorise: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                selected: [],
                accounts: [],
                page: 1,
                gatewayWebhookBaseUri: '',
                totalRows: 0,
                item: null,
            };
        },
        computed: {
            canEdit() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_21');
            },
        },
        created() {
            if (this.authorise) {
                const account = window.localStorage.getItem('trustpilotAccount');
                window.localStorage.removeItem('trustpilotAccount');

                if (!account) {
                    showErrorMessage(this.$t('trustpilotSaveError'));
                    this.loadTable();
                } else {
                    this.createAccount(account);
                }
            } else {
                this.loadTable();
            }
        },
        methods: {
            createAccount(account) {
                const urlParams = new URLSearchParams(window.location.search);
                save(
                    'trustpilot/update',
                    {
                        data: account,
                        code: urlParams.get('code'),
                    },
                    this.loadTable,
                );
            },
            // Load table of search results
            loadTable() {
                load('trustpilot/get', { page: this.page }, (response) => {
                    this.gatewayWebhookBaseUri = response.gatewayWebhookBaseUri;
                    this.totalRows = response.totalRows;
                    this.accounts = response.accounts;
                });
            },

            openForm(accountId) {
                if (accountId > 0) {
                    load('trustpilot/edit', { accountId: accountId }, (response) => {
                        this.item = response.data;
                    });
                } else {
                    this.item = {
                        accountId: null,
                        organisationId: this.$store.user.orgId,
                        accountName: '',
                        apiKey: '',
                        apiSecret: '',
                    };
                }
            },

            handleDelete() {
                deleteItems('trustpilot/delete', { accountIds: this.selected }, () => {
                    this.selected = [];
                    this.loadTable();
                });
            },

            saveItem() {
                if (!this.item.accountName || !this.item.apiKey || !this.item.apiSecret) {
                    showValidationMessage('Invalid form data');
                    return;
                }

                this.$loading({ text: this.$t('Redirecting to Trustpilot for authentication...') });
                const data = JSON.stringify(this.item);
                const apiKey = this.item.apiKey.trim();
                this.item = null;
                setTimeout(() => {
                    this.closePopup();
                    this.selected = [];
                    window.localStorage.setItem('trustpilotAccount', data);
                    window.location.assign(
                        `https://authenticate.trustpilot.com?response_type=type&client_id=${apiKey}&redirect_uri=` +
                            baseUrl() +
                            '/trustpilot/authorise',
                    );
                }, 2000);
            },

            formatWebhookUri(key, secret, organisationId, accountId) {
                let uriParts = this.gatewayWebhookBaseUri.match(/^(https?:\/\/)(.*)/i);
                return (
                    uriParts[1] +
                    key +
                    ':' +
                    secret +
                    '@' +
                    uriParts[2] +
                    '/' +
                    organisationId +
                    '/' +
                    accountId +
                    '/review'
                );
            },

            copyWebhookUrl(account) {
                const text = this.formatWebhookUri(
                    account.apiKey,
                    account.apiSecret,
                    account.organisationId,
                    account.accountId,
                );
                const elem = document.createElement('textarea');
                document.body.appendChild(elem);
                elem.value = text;
                elem.select();
                document.execCommand('copy');
                document.body.removeChild(elem);
            },
        },
    };
</script>
